import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';

const AuthContext = createContext();

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 minutes of inactivity before warning
const WARNING_DURATION = 120; // 2 minutes (120 seconds) countdown warning
const LAST_ACTIVITY_KEY = 'auth_last_activity';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Inactivity & Warning States
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(WARNING_DURATION);

  const checkIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Clear session state
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setShowIdleModal(false);

    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
  }, []);

  // Update last activity timestamp in state and localStorage
  const updateActivityTimestamp = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }, []);

  // Check activity status periodically
  const checkInactivity = useCallback(() => {
    if (!token) return;

    const storedLastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const lastActive = storedLastActivity ? parseInt(storedLastActivity, 10) : lastActivityRef.current;
    const now = Date.now();
    const elapsed = now - lastActive;

    const totalAllowedTime = INACTIVITY_LIMIT + WARNING_DURATION * 1000;

    if (elapsed >= totalAllowedTime) {
      // User exceeded 10 mins idle + 2 mins warning -> Logout
      logout();
    } else if (elapsed >= INACTIVITY_LIMIT) {
      // User exceeded 10 mins idle -> Show warning modal and update remaining seconds
      const remainingSeconds = Math.ceil((totalAllowedTime - elapsed) / 1000);
      setShowIdleModal(true);
      setIdleCountdown(Math.max(0, remainingSeconds));
    } else {
      // User is active within 10 mins -> Ensure modal is hidden
      setShowIdleModal(false);
    }
  }, [token, logout]);

  // Initial session load & auto-logout check on return
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      const storedLastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
      const lastActive = storedLastActivity ? parseInt(storedLastActivity, 10) : Date.now();
      const elapsed = Date.now() - lastActive;

      // If user was away longer than 12 mins (10 idle + 2 warning) while tab was closed -> Logout immediately
      if (elapsed >= INACTIVITY_LIMIT + WARNING_DURATION * 1000) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      } else {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Failed to parse user data from storage', e);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
    }
    setLoading(false);
  }, []);

  // Synchronize authentication and activity across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setToken(e.newValue);
      }
      if (e.key === 'user') {
        try {
          setUser(e.newValue ? JSON.parse(e.newValue) : null);
        } catch (e) {
          setUser(null);
        }
      }
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        lastActivityRef.current = parseInt(e.newValue, 10);
        checkInactivity();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkInactivity]);

  // Main inactivity tracking interval
  useEffect(() => {
    if (!token) {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      setShowIdleModal(false);
      return;
    }

    // Set initial activity timestamp if not present
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      updateActivityTimestamp();
    }

    // Check inactivity every second
    checkIntervalRef.current = setInterval(checkInactivity, 1000);

    // Throttle user activity tracking so it doesn't slam localStorage on mouse movements
    let throttleTimeout = null;
    const handleUserActivity = () => {
      // Do not reset activity if warning modal is open
      if (showIdleModal) return;

      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          updateActivityTimestamp();
          throttleTimeout = null;
        }, 1000); // Max once per second
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, handleUserActivity));

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (throttleTimeout) clearTimeout(throttleTimeout);
      events.forEach((event) => window.removeEventListener(event, handleUserActivity));
    };
  }, [token, showIdleModal, checkInactivity, updateActivityTimestamp]);

  const loginWithToken = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    updateActivityTimestamp();
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const stayLoggedIn = () => {
    setShowIdleModal(false);
    updateActivityTimestamp();
  };

  // Context value memoization
  const value = useMemo(
    () => ({
      user,
      token,
      loginWithToken,
      updateUser,
      logout,
      loading,
      showIdleModal,
      idleCountdown,
      stayLoggedIn,
    }),
    [user, token, loading, showIdleModal, idleCountdown, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}

      {/* Auto Logout Warning Modal */}
      {showIdleModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ margin: '0 0 10px 0' }}>Are you still there?</h3>
            <p>
              You have been inactive for 10 minutes. For security, you will be logged out in{' '}
              <strong>{idleCountdown} seconds</strong>.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={logout} style={logoutBtnStyle}>
                Log Out Now
              </button>
              <button onClick={stayLoggedIn} style={stayBtnStyle}>
                I'm Still Here
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Inline Styles
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalContentStyle = {
  background: '#ffffff',
  padding: '24px',
  borderRadius: '8px',
  maxWidth: '400px',
  width: '90%',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const stayBtnStyle = {
  padding: '8px 16px',
  backgroundColor: '#2563eb',
  color: '#ffffff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const logoutBtnStyle = {
  padding: '8px 16px',
  backgroundColor: '#e11d48',
  color: '#ffffff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};